import { Box, type SxProps, type Theme } from '@mui/material';
import type React from 'react';

interface SelectableListItemProps {
    isSelected?: boolean;
    onClick?: () => void;
    disabled?: boolean;
    children: React.ReactNode;
    /** Override or extend the root sx. */
    sx?: SxProps<Theme>;
    'data-testid'?: string;
}

/**
 * Generic selectable row wrapper used across workflow list items
 * (images, structure sets, registrations, doses, etc.).
 *
 * Provides the shared selection chrome:
 * - Left accent bar (::before)
 * - Selected / hover background tints
 * - Smooth transitions
 */
export default function SelectableListItem({
    isSelected = false,
    onClick,
    disabled,
    children,
    sx,
    'data-testid': testId,
}: SelectableListItemProps) {
    return (
        <Box
            data-testid={testId}
            onClick={disabled ? undefined : onClick}
            sx={[
                {
                    position: 'relative',
                    px: 1.5,
                    py: 0.75,
                    cursor: onClick && !disabled ? 'pointer' : 'inherit',
                    bgcolor: isSelected
                        ? 'rgba(144, 202, 249, 0.08)'
                        : 'rgba(255, 255, 255, 0.02)',
                    '&:hover':
                        onClick && !disabled
                            ? {
                                  bgcolor: isSelected
                                      ? 'rgba(144, 202, 249, 0.12)'
                                      : 'rgba(144, 202, 249, 0.08)',
                              }
                            : undefined,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        bgcolor: isSelected
                            ? 'primary.main'
                            : 'rgba(144, 202, 249, 0.2)',
                        transition: 'background-color 0.15s ease-in-out',
                        zIndex: 1,
                    },
                    transition: 'all 0.15s ease-in-out',
                    ...(disabled && {
                        opacity: 0.5,
                        pointerEvents: 'none' as const,
                    }),
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {children}
        </Box>
    );
}
